import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CancellationEffectiveAtWithLiterals,
  CreateOfflineOrderApplicationErrors,
  CreateOfflineOrderOptions,
  CreateOfflineOrderResponse,
  CreateOfflineOrderValidationErrors,
  CreateOnlineOrderApplicationErrors,
  CreateOnlineOrderOptions,
  CreateOnlineOrderResponse,
  CreateOnlineOrderValidationErrors,
  GetOfflineOrderPreviewApplicationErrors,
  GetOfflineOrderPreviewOptions,
  GetOfflineOrderPreviewResponse,
  GetOfflineOrderPreviewValidationErrors,
  GetOnlineOrderPreviewApplicationErrors,
  GetOnlineOrderPreviewOptions,
  GetOnlineOrderPreviewResponse,
  GetOnlineOrderPreviewValidationErrors,
  GetOrderResponse,
  GetPricePreviewApplicationErrors,
  GetPricePreviewOptions,
  GetPricePreviewResponse,
  ListOrdersResponse,
  ManagementGetOrderOptions,
  ManagementListOrdersOptions,
  MemberGetOrderOptions,
  MemberListOrdersOptions,
  MemberListOrdersResponse,
  Order,
  OrderAutoRenewCanceledEnvelope,
  OrderCanceledEnvelope,
  OrderCreatedEnvelope,
  OrderCycleStartedEnvelope,
  OrderEndDatePostponedEnvelope,
  OrderEndedEnvelope,
  OrderMarkedAsPaidEnvelope,
  OrderPausedEnvelope,
  OrderPurchasedEnvelope,
  OrderResumedEnvelope,
  OrderStartDateChangedEnvelope,
  OrderStartedEnvelope,
  OrderUpdatedEnvelope,
  cancelOrder as universalCancelOrder,
  createOfflineOrder as universalCreateOfflineOrder,
  createOnlineOrder as universalCreateOnlineOrder,
  getOfflineOrderPreview as universalGetOfflineOrderPreview,
  getOnlineOrderPreview as universalGetOnlineOrderPreview,
  getPricePreview as universalGetPricePreview,
  managementGetOrder as universalManagementGetOrder,
  managementListOrders as universalManagementListOrders,
  markAsPaid as universalMarkAsPaid,
  memberGetOrder as universalMemberGetOrder,
  memberListOrders as universalMemberListOrders,
  pauseOrder as universalPauseOrder,
  postponeEndDate as universalPostponeEndDate,
  requestCancellation as universalRequestCancellation,
  resumeOrder as universalResumeOrder,
} from './pricing-plans-v2-order-orders.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/pricing-plans' };

export function managementGetOrder(
  httpClient: HttpClient
): ManagementGetOrderSignature {
  return (_id: string, options?: ManagementGetOrderOptions) =>
    universalManagementGetOrder(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ManagementGetOrderSignature {
  /**
   * Retrieves an order by ID.
   * @param - Order ID.
   * @param - Options to use when getting an order.
   */
  (_id: string, options?: ManagementGetOrderOptions): Promise<
    NonNullablePaths<
      GetOrderResponse,
      | `order._id`
      | `order.planId`
      | `order.subscriptionId`
      | `order.buyer.memberId`
      | `order.buyer.contactId`
      | `order.priceDetails.subscription.cycleDuration.unit`
      | `order.priceDetails.subtotal`
      | `order.priceDetails.discount`
      | `order.priceDetails.tax.name`
      | `order.priceDetails.tax.includedInPrice`
      | `order.priceDetails.tax.rate`
      | `order.priceDetails.tax.amount`
      | `order.priceDetails.total`
      | `order.priceDetails.planPrice`
      | `order.priceDetails.currency`
      | `order.priceDetails.coupon.code`
      | `order.priceDetails.coupon.amount`
      | `order.priceDetails.coupon._id`
      | `order.pricing.prices`
      | `order.pricing.prices.${number}.duration.cycleFrom`
      | `order.pricing.prices.${number}.price.subtotal`
      | `order.pricing.prices.${number}.price.discount`
      | `order.pricing.prices.${number}.price.total`
      | `order.pricing.prices.${number}.price.currency`
      | `order.pricing.prices.${number}.price.proration`
      | `order.type`
      | `order.status`
      | `order.cancellation.cause`
      | `order.cancellation.effectiveAt`
      | `order.lastPaymentStatus`
      | `order.pausePeriods`
      | `order.pausePeriods.${number}.status`
      | `order.currentCycle.index`
      | `order.planName`
      | `order.planDescription`
      | `order.planPrice`,
      7
    >
  >;
}

export function managementListOrders(
  httpClient: HttpClient
): ManagementListOrdersSignature {
  return (options?: ManagementListOrdersOptions) =>
    universalManagementListOrders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ManagementListOrdersSignature {
  /**
   * Retrieves a list of up to 50 pricing plan orders and details, given the specified sorting and filtering.
   *
   * By default, this endpoint will retrieve all orders and return them sorted by `createdDate` in `DESC`, descending order.
   * `sort.fieldName` supports `endDate` and `createdDate` fields and defaults to `ASC`, ascending order.
   * @param - Filtering, sorting, and pagination options.
   */
  (options?: ManagementListOrdersOptions): Promise<
    NonNullablePaths<
      ListOrdersResponse,
      | `orders`
      | `orders.${number}._id`
      | `orders.${number}.planId`
      | `orders.${number}.subscriptionId`
      | `orders.${number}.buyer.memberId`
      | `orders.${number}.buyer.contactId`
      | `orders.${number}.priceDetails.singlePaymentForDuration.unit`
      | `orders.${number}.priceDetails.subtotal`
      | `orders.${number}.priceDetails.discount`
      | `orders.${number}.priceDetails.tax.name`
      | `orders.${number}.priceDetails.tax.includedInPrice`
      | `orders.${number}.priceDetails.tax.rate`
      | `orders.${number}.priceDetails.tax.amount`
      | `orders.${number}.priceDetails.total`
      | `orders.${number}.priceDetails.planPrice`
      | `orders.${number}.priceDetails.currency`
      | `orders.${number}.priceDetails.coupon.code`
      | `orders.${number}.priceDetails.coupon.amount`
      | `orders.${number}.priceDetails.coupon._id`
      | `orders.${number}.type`
      | `orders.${number}.status`
      | `orders.${number}.cancellation.cause`
      | `orders.${number}.cancellation.effectiveAt`
      | `orders.${number}.lastPaymentStatus`
      | `orders.${number}.currentCycle.index`
      | `orders.${number}.planName`
      | `orders.${number}.planDescription`
      | `orders.${number}.planPrice`,
      6
    >
  >;
}

export function postponeEndDate(
  httpClient: HttpClient
): PostponeEndDateSignature {
  return (_id: string, endDate: Date) =>
    universalPostponeEndDate(
      _id,
      endDate,
      // @ts-ignore
      { httpClient }
    );
}

interface PostponeEndDateSignature {
  /**
   * Extends the duration of a pricing plan order by postponing the order's `endDate`. Postponing the end date of an order does not impact payments.
   *
   * New `endDate` must be later than the order's current `endDate`. Can't postpone orders that are unlimited.
   * Can't postpone an order with `status`: `PAUSED`.
   * @param - Order ID.
   * @param - New end date and time.
   *
   * Must be later than the current end date and time.
   * @param - Options for postponing the end date of an order.
   */
  (_id: string, endDate: Date): Promise<void>;
}

export function cancelOrder(httpClient: HttpClient): CancelOrderSignature {
  return (_id: string, effectiveAt: CancellationEffectiveAtWithLiterals) =>
    universalCancelOrder(
      _id,
      effectiveAt,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelOrderSignature {
  /**
   * Cancels an existing order.
   *
   * For orders with recurring payments, a cancellation can be set to occur either `IMMEDIATELY` or at the `NEXT_PAYMENT_DATE`.
   * For orders with one-time payments, a cancellation can only be set for `IMMEDIATELY`.
   *
   * #### Canceling during the free trial period.
   *
   * When a buyer cancels their order during the free trial period, the buyer's subscription expires at the end
   * of the free trial period and they will not be billed. The buyer may continue using the benefits until the end
   * of the free trial period.
   *
   * When a Wix user cancels an ordered plan during the free trial period, they choose whether to apply the cancellation
   * `IMMEDIATELY` or at the `NEXT_PAYMENT_DATE`. Canceling `IMMEDIATELY` will end the subscription for the buyer
   * immediately, even during the free trial period and the buyer won't be billed. Canceling at the
   * `NEXT_PAYMENT_DATE` allows the buyer to continue using the benefits of the subscription until the end of the free trial period.
   * Then, the subscription ends and the buyer is not billed.
   * @param - Order ID.
   * @param - __Required.__ When the order will be canceled. One-time orders can only be canceled `IMMEDIATELY`.
   * @param - Options for canceling orders.
   */
  (
    _id: string,
    effectiveAt: CancellationEffectiveAtWithLiterals
  ): Promise<void>;
}

export function markAsPaid(httpClient: HttpClient): MarkAsPaidSignature {
  return (_id: string) =>
    universalMarkAsPaid(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface MarkAsPaidSignature {
  /**
   * Marks an offline order as paid.
   * > __Note__: Marking separate payment cycles as paid is not yet supported. The entire order will be marked as paid. Subsequent offline payments do trigger events and emails, but are not registered as additional offline payments.
   *
   * Marking an offline order as paid causes the following changes:
   * - The order's `lastPaymentStatus` changes to `"PAID"`.
   * - The order's status changes to either `"PENDING"` or `"ACTIVE"`, depending on the order's `startDate`.
   *
   * An error occurs if you attempt to:
   * - Mark an already-paid, offline order as paid. You cannot mark an offline order as paid twice.
   * - Mark an online order as paid. The Mark as Paid method is supported for offline orders only.
   * @param - Order ID.
   */
  (_id: string): Promise<void>;
}

export function pauseOrder(httpClient: HttpClient): PauseOrderSignature {
  return (_id: string) =>
    universalPauseOrder(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface PauseOrderSignature {
  /**
   * Pauses an order. Calling this method changes the order status to `"PAUSED"` and updates the `pausePeriods` array.
   *
   * Only orders with `status`: `ACTIVE` can be paused.
   * For orders with recurring payments, it also pauses the payment schedule. Buyers are not charged when an order is paused.
   * Pausing an order affects the end date of the order by adding the time the order is paused to the `endDate`.
   * The `endDate` and the `earliestEndDate` for the order are adjusted to include the pause period when the order is resumed.
   *
   * To resume a paused order, call Resume Order.
   * @param - Order ID.
   */
  (_id: string): Promise<void>;
}

export function resumeOrder(httpClient: HttpClient): ResumeOrderSignature {
  return (_id: string) =>
    universalResumeOrder(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface ResumeOrderSignature {
  /**
   * Resumes a paused order. For orders with recurring payments, it also restarts the payment schedule.
   *
   * Resuming an order causes the following changes:
   * - The order status changes to `"ACTIVE"`.
   * - The `pausePeriods` array is updated.
   * - The `endDate` for the order is adjusted to include the pause period.
   * - For orders with recurring payments, the payment schedule is restarted.
   * - The `earliestEndDate` is adjusted to include the pause period. (This property is reserved for future use).
   *
   * To pause an order, call Pause Order.
   * @param - Order ID.
   */
  (_id: string): Promise<void>;
}

export function createOnlineOrder(
  httpClient: HttpClient
): CreateOnlineOrderSignature {
  return (
    planId: string,
    options?: NonNullablePaths<CreateOnlineOrderOptions, `onBehalf.memberId`, 3>
  ) =>
    universalCreateOnlineOrder(
      planId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateOnlineOrderSignature {
  /**
   * Creates an online order for a site member.
   *
   * If this method is called by a [site member](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member), the plan is automatically ordered on behalf of that site member. Otherwise, you must specify `onBehalf.memberId` in your call.
   *
   * When an online order is created, but payment hasn't been processed, its status is set to `DRAFT`. After the payment has been processed, if the start date is in the future the order's status is set to `PENDING`. Otherwise, it's set to `ACTIVE`.
   * @param - Plan ID.
   */
  (
    planId: string,
    options?: NonNullablePaths<CreateOnlineOrderOptions, `onBehalf.memberId`, 3>
  ): Promise<
    NonNullablePaths<
      CreateOnlineOrderResponse,
      | `order._id`
      | `order.planId`
      | `order.subscriptionId`
      | `order.buyer.memberId`
      | `order.buyer.contactId`
      | `order.priceDetails.subscription.cycleDuration.unit`
      | `order.priceDetails.subtotal`
      | `order.priceDetails.discount`
      | `order.priceDetails.tax.name`
      | `order.priceDetails.tax.includedInPrice`
      | `order.priceDetails.tax.rate`
      | `order.priceDetails.tax.amount`
      | `order.priceDetails.total`
      | `order.priceDetails.planPrice`
      | `order.priceDetails.currency`
      | `order.priceDetails.coupon.code`
      | `order.priceDetails.coupon.amount`
      | `order.priceDetails.coupon._id`
      | `order.pricing.prices`
      | `order.pricing.prices.${number}.duration.cycleFrom`
      | `order.pricing.prices.${number}.price.subtotal`
      | `order.pricing.prices.${number}.price.discount`
      | `order.pricing.prices.${number}.price.total`
      | `order.pricing.prices.${number}.price.currency`
      | `order.pricing.prices.${number}.price.proration`
      | `order.type`
      | `order.status`
      | `order.cancellation.cause`
      | `order.cancellation.effectiveAt`
      | `order.lastPaymentStatus`
      | `order.pausePeriods`
      | `order.pausePeriods.${number}.status`
      | `order.currentCycle.index`
      | `order.planName`
      | `order.planDescription`
      | `order.planPrice`,
      7
    > & {
      __applicationErrorsType?: CreateOnlineOrderApplicationErrors;
      __validationErrorsType?: CreateOnlineOrderValidationErrors;
    }
  >;
}

export function createOfflineOrder(
  httpClient: HttpClient
): CreateOfflineOrderSignature {
  return (
    planId: string,
    memberId: string,
    options?: CreateOfflineOrderOptions
  ) =>
    universalCreateOfflineOrder(
      planId,
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateOfflineOrderSignature {
  /**
   * Creates an order for a buyer who purchased the plan with an offline transaction.
   *
   * An offline order is handled off of the Wix site and is marked as `type`: `offline`. If a pricing plan
   * has a limit on the amount of purchases per buyer, that limit is ignored for offline orders.
   * Tax is only applied if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).
   *
   * When creating a free offline order:
   * The order's status is set to `"PENDING"` if the start date is in the future. Otherwise, the status is set to `"ACTIVE"`.
   * The order's last payment status is set to `"NOT_APPLICABLE"`. "
   *
   * When creating a non-free offline order:
   * The order's status is set to `"PENDING"` if the start date is in the future. Otherwise, the status is set to `"ACTIVE"`.
   * The order's last payment status is set to `"UNPAID"` or `"PAID"` based on the data passed in the `paid` boolean in the request.
   *
   * Payment for an offline order can be set in 1 of 2 ways:
   * + During order creation, set `paid`: `true`.
   * + After creation, call Mark As Paid.
   * @param - ID of the plan being ordered, from the Plans API.
   * @param - ID of the member ordering the plan, from the Members API.
   * @param - Options for creating an offline order.
   */
  (
    planId: string,
    memberId: string,
    options?: CreateOfflineOrderOptions
  ): Promise<
    NonNullablePaths<
      CreateOfflineOrderResponse,
      | `order._id`
      | `order.planId`
      | `order.subscriptionId`
      | `order.buyer.memberId`
      | `order.buyer.contactId`
      | `order.priceDetails.subscription.cycleDuration.unit`
      | `order.priceDetails.subtotal`
      | `order.priceDetails.discount`
      | `order.priceDetails.tax.name`
      | `order.priceDetails.tax.includedInPrice`
      | `order.priceDetails.tax.rate`
      | `order.priceDetails.tax.amount`
      | `order.priceDetails.total`
      | `order.priceDetails.planPrice`
      | `order.priceDetails.currency`
      | `order.priceDetails.coupon.code`
      | `order.priceDetails.coupon.amount`
      | `order.priceDetails.coupon._id`
      | `order.pricing.prices`
      | `order.pricing.prices.${number}.duration.cycleFrom`
      | `order.pricing.prices.${number}.price.subtotal`
      | `order.pricing.prices.${number}.price.discount`
      | `order.pricing.prices.${number}.price.total`
      | `order.pricing.prices.${number}.price.currency`
      | `order.pricing.prices.${number}.price.proration`
      | `order.type`
      | `order.status`
      | `order.cancellation.cause`
      | `order.cancellation.effectiveAt`
      | `order.lastPaymentStatus`
      | `order.pausePeriods`
      | `order.pausePeriods.${number}.status`
      | `order.currentCycle.index`
      | `order.planName`
      | `order.planDescription`
      | `order.planPrice`,
      7
    > & {
      __applicationErrorsType?: CreateOfflineOrderApplicationErrors;
      __validationErrorsType?: CreateOfflineOrderValidationErrors;
    }
  >;
}

export function getOnlineOrderPreview(
  httpClient: HttpClient
): GetOnlineOrderPreviewSignature {
  return (planId: string, options?: GetOnlineOrderPreviewOptions) =>
    universalGetOnlineOrderPreview(
      planId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOnlineOrderPreviewSignature {
  /**
   * Returns an `order` object that represents a potential online order for a site member.
   *
   * You can use this method to show a site member a preview of an online order before [creating](https://dev.wix.com/docs/rest/business-solutions/pricing-plans/pricing-plans/orders/create-online-order) it.
   *
   * This method must be called using the [site member identity](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member). Therefore, [Wix apps](https://dev.wix.com/docs/build-apps) can't currently call this method using REST.
   * @param - Plan ID.
   */
  (planId: string, options?: GetOnlineOrderPreviewOptions): Promise<
    NonNullablePaths<
      GetOnlineOrderPreviewResponse,
      | `order._id`
      | `order.planId`
      | `order.subscriptionId`
      | `order.buyer.memberId`
      | `order.buyer.contactId`
      | `order.priceDetails.subscription.cycleDuration.unit`
      | `order.priceDetails.subtotal`
      | `order.priceDetails.discount`
      | `order.priceDetails.tax.name`
      | `order.priceDetails.tax.includedInPrice`
      | `order.priceDetails.tax.rate`
      | `order.priceDetails.tax.amount`
      | `order.priceDetails.total`
      | `order.priceDetails.planPrice`
      | `order.priceDetails.currency`
      | `order.priceDetails.coupon.code`
      | `order.priceDetails.coupon.amount`
      | `order.priceDetails.coupon._id`
      | `order.pricing.prices`
      | `order.pricing.prices.${number}.duration.cycleFrom`
      | `order.pricing.prices.${number}.price.subtotal`
      | `order.pricing.prices.${number}.price.discount`
      | `order.pricing.prices.${number}.price.total`
      | `order.pricing.prices.${number}.price.currency`
      | `order.pricing.prices.${number}.price.proration`
      | `order.type`
      | `order.status`
      | `order.cancellation.cause`
      | `order.cancellation.effectiveAt`
      | `order.lastPaymentStatus`
      | `order.pausePeriods`
      | `order.pausePeriods.${number}.status`
      | `order.currentCycle.index`
      | `order.planName`
      | `order.planDescription`
      | `order.planPrice`
      | `purchaseLimitExceeded`,
      7
    > & {
      __applicationErrorsType?: GetOnlineOrderPreviewApplicationErrors;
      __validationErrorsType?: GetOnlineOrderPreviewValidationErrors;
    }
  >;
}

export function getOfflineOrderPreview(
  httpClient: HttpClient
): GetOfflineOrderPreviewSignature {
  return (
    planId: string,
    memberId: string,
    options?: GetOfflineOrderPreviewOptions
  ) =>
    universalGetOfflineOrderPreview(
      planId,
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOfflineOrderPreviewSignature {
  /**
   * Performs a dry run of a purchase and provides an order preview.
   *
   * The preview uses the same logic as purchasing a plan, but the preview is not saved. Because an order is not actually
   * created, the preview order's `orderId` and `subscriptionId` are displayed as a string of multiple zero characters
   * (`000000-0000`). Tax is only calculated if the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection).
   * <br>
   * If a pricing plan has a limit on the amount of purchases per buyer, that limit is not considered for generating the preview.
   * But, if that limit has been reached and this order would then exceed the amount of purchases permitted for this buyer, then
   * `purchaseLimitExceeded` will return as `true`.
   *
   * To get a general price preview for a plan that's not buyer-specific, call Get Price Preview.
   * @param - Member ID of the buyer the previewed order is for, from the Members API.
   * @param - Options for previewing the offline order.
   * @param - ID of the plan of the previewed order.
   */
  (
    planId: string,
    memberId: string,
    options?: GetOfflineOrderPreviewOptions
  ): Promise<
    NonNullablePaths<
      GetOfflineOrderPreviewResponse,
      | `order._id`
      | `order.planId`
      | `order.subscriptionId`
      | `order.buyer.memberId`
      | `order.buyer.contactId`
      | `order.priceDetails.subscription.cycleDuration.unit`
      | `order.priceDetails.subtotal`
      | `order.priceDetails.discount`
      | `order.priceDetails.tax.name`
      | `order.priceDetails.tax.includedInPrice`
      | `order.priceDetails.tax.rate`
      | `order.priceDetails.tax.amount`
      | `order.priceDetails.total`
      | `order.priceDetails.planPrice`
      | `order.priceDetails.currency`
      | `order.priceDetails.coupon.code`
      | `order.priceDetails.coupon.amount`
      | `order.priceDetails.coupon._id`
      | `order.pricing.prices`
      | `order.pricing.prices.${number}.duration.cycleFrom`
      | `order.pricing.prices.${number}.price.subtotal`
      | `order.pricing.prices.${number}.price.discount`
      | `order.pricing.prices.${number}.price.total`
      | `order.pricing.prices.${number}.price.currency`
      | `order.pricing.prices.${number}.price.proration`
      | `order.type`
      | `order.status`
      | `order.cancellation.cause`
      | `order.cancellation.effectiveAt`
      | `order.lastPaymentStatus`
      | `order.pausePeriods`
      | `order.pausePeriods.${number}.status`
      | `order.currentCycle.index`
      | `order.planName`
      | `order.planDescription`
      | `order.planPrice`
      | `purchaseLimitExceeded`,
      7
    > & {
      __applicationErrorsType?: GetOfflineOrderPreviewApplicationErrors;
      __validationErrorsType?: GetOfflineOrderPreviewValidationErrors;
    }
  >;
}

export function getPricePreview(
  httpClient: HttpClient
): GetPricePreviewSignature {
  return (planId: string, options?: GetPricePreviewOptions) =>
    universalGetPricePreview(
      planId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPricePreviewSignature {
  /**
   * Retrieves a plan's pricing.
   *
   * The price preview uses the same logic as purchasing a plan, but the preview is not saved. Tax is only applied if
   * the site [has it configured](https://support.wix.com/en/article/pricing-plans-setting-up-tax-collection). The price is returned
   * in the pricing model format used for orders. Learn more about [pricing models](https://dev.wix.com/api/rest/wix-pricing-plans/pricing-plans/introduction#wix-pricing-plans_pricing-plans_introduction_pricing-models).
   *
   * Buyers do not have to be logged in to preview the price, and as such, the details returned are not buyer-specific. To
   * generate a preview of a purchase for a specific buyer, call Get Offline Order Preview.
   * @param - ID of plan to preview.
   * @param - Options for getting a price preview.
   */
  (planId: string, options?: GetPricePreviewOptions): Promise<
    NonNullablePaths<
      GetPricePreviewResponse,
      | `price.subscription.cycleDuration.unit`
      | `price.subtotal`
      | `price.discount`
      | `price.tax.name`
      | `price.tax.includedInPrice`
      | `price.tax.rate`
      | `price.tax.amount`
      | `price.total`
      | `price.planPrice`
      | `price.currency`
      | `price.coupon.code`
      | `price.coupon.amount`
      | `price.coupon._id`
      | `prices`
      | `prices.${number}.duration.cycleFrom`
      | `prices.${number}.price.subtotal`
      | `prices.${number}.price.coupon.code`
      | `prices.${number}.price.coupon.amount`
      | `prices.${number}.price.coupon._id`
      | `prices.${number}.price.discount`
      | `prices.${number}.price.tax.name`
      | `prices.${number}.price.tax.includedInPrice`
      | `prices.${number}.price.tax.rate`
      | `prices.${number}.price.tax.amount`
      | `prices.${number}.price.total`
      | `prices.${number}.price.currency`
      | `prices.${number}.price.proration`,
      6
    > & {
      __applicationErrorsType?: GetPricePreviewApplicationErrors;
    }
  >;
}

export function memberGetOrder(
  httpClient: HttpClient
): MemberGetOrderSignature {
  return (_id: string, options?: MemberGetOrderOptions) =>
    universalMemberGetOrder(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MemberGetOrderSignature {
  /**
   * Retrieves an order for the currently logged-in member by ID.
   * @param - Order ID.
   * @param - Options for getting a logged-in member's order.
   * @returns Requested order.
   */
  (_id: string, options?: MemberGetOrderOptions): Promise<
    NonNullablePaths<
      Order,
      | `_id`
      | `planId`
      | `subscriptionId`
      | `buyer.memberId`
      | `buyer.contactId`
      | `priceDetails.subscription.cycleDuration.unit`
      | `priceDetails.subtotal`
      | `priceDetails.discount`
      | `priceDetails.tax.name`
      | `priceDetails.tax.includedInPrice`
      | `priceDetails.tax.rate`
      | `priceDetails.tax.amount`
      | `priceDetails.total`
      | `priceDetails.planPrice`
      | `priceDetails.currency`
      | `priceDetails.coupon.code`
      | `priceDetails.coupon.amount`
      | `priceDetails.coupon._id`
      | `pricing.prices`
      | `pricing.prices.${number}.duration.cycleFrom`
      | `pricing.prices.${number}.price.subtotal`
      | `pricing.prices.${number}.price.discount`
      | `pricing.prices.${number}.price.total`
      | `pricing.prices.${number}.price.currency`
      | `pricing.prices.${number}.price.proration`
      | `type`
      | `status`
      | `cancellation.cause`
      | `cancellation.effectiveAt`
      | `lastPaymentStatus`
      | `pausePeriods`
      | `pausePeriods.${number}.status`
      | `currentCycle.index`
      | `planName`
      | `planDescription`
      | `planPrice`,
      6
    >
  >;
}

export function memberListOrders(
  httpClient: HttpClient
): MemberListOrdersSignature {
  return (options?: MemberListOrdersOptions) =>
    universalMemberListOrders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MemberListOrdersSignature {
  /**
   * Retrieves a list of up to 100 pricing plan orders for currently logged-in member.
   * @param - Filtering, sorting, and pagination options.
   */
  (options?: MemberListOrdersOptions): Promise<
    NonNullablePaths<
      MemberListOrdersResponse,
      | `orders`
      | `orders.${number}._id`
      | `orders.${number}.planId`
      | `orders.${number}.subscriptionId`
      | `orders.${number}.buyer.memberId`
      | `orders.${number}.buyer.contactId`
      | `orders.${number}.priceDetails.singlePaymentForDuration.unit`
      | `orders.${number}.priceDetails.subtotal`
      | `orders.${number}.priceDetails.discount`
      | `orders.${number}.priceDetails.tax.name`
      | `orders.${number}.priceDetails.tax.includedInPrice`
      | `orders.${number}.priceDetails.tax.rate`
      | `orders.${number}.priceDetails.tax.amount`
      | `orders.${number}.priceDetails.total`
      | `orders.${number}.priceDetails.planPrice`
      | `orders.${number}.priceDetails.currency`
      | `orders.${number}.priceDetails.coupon.code`
      | `orders.${number}.priceDetails.coupon.amount`
      | `orders.${number}.priceDetails.coupon._id`
      | `orders.${number}.type`
      | `orders.${number}.status`
      | `orders.${number}.cancellation.cause`
      | `orders.${number}.cancellation.effectiveAt`
      | `orders.${number}.lastPaymentStatus`
      | `orders.${number}.currentCycle.index`
      | `orders.${number}.planName`
      | `orders.${number}.planDescription`
      | `orders.${number}.planPrice`,
      6
    >
  >;
}

export function requestCancellation(
  httpClient: HttpClient
): RequestCancellationSignature {
  return (_id: string, effectiveAt: CancellationEffectiveAtWithLiterals) =>
    universalRequestCancellation(
      _id,
      effectiveAt,
      // @ts-ignore
      { httpClient }
    );
}

interface RequestCancellationSignature {
  /**
   * Cancels an order. Recurring orders can be canceled either immediately or at the next payment date. One time orders can only be canceled immediately.
   *
   * There may be some operations that continue to be processed before the status of the order is changed to `"CANCELED"`. For example, payments might need to be refunded before the order is fully canceled.
   *
   * Canceling during the free trial period: When a buyer cancels their order during the free trial period, the buyer's subscription expires at the end of the free trial period and they won't be billed. The buyer may continue using the benefits until the end of the free trial period.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
   * @param - Order ID.
   * @param - Required. Whether to cancel the order effective immediately or at the next payment date. One-time orders can only be canceled immediately.
   * @param - Options for requesting a cancellation.
   */
  (
    _id: string,
    effectiveAt: CancellationEffectiveAtWithLiterals
  ): Promise<void>;
}

export const onOrderAutoRenewCanceled = EventDefinition(
  'wix.pricing_plans.v2.order_auto_renew_canceled',
  true,
  (event: OrderAutoRenewCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderAutoRenewCanceledEnvelope>();
export const onOrderCanceled = EventDefinition(
  'wix.pricing_plans.v2.order_canceled',
  true,
  (event: OrderCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderCanceledEnvelope>();
export const onOrderCycleStarted = EventDefinition(
  'wix.pricing_plans.v2.order_cycle_started',
  true,
  (event: OrderCycleStartedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderCycleStartedEnvelope>();
export const onOrderEndDatePostponed = EventDefinition(
  'wix.pricing_plans.v2.order_end_date_postponed',
  true,
  (event: OrderEndDatePostponedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderEndDatePostponedEnvelope>();
export const onOrderEnded = EventDefinition(
  'wix.pricing_plans.v2.order_ended',
  true,
  (event: OrderEndedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderEndedEnvelope>();
export const onOrderMarkedAsPaid = EventDefinition(
  'wix.pricing_plans.v2.order_marked_as_paid',
  true,
  (event: OrderMarkedAsPaidEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderMarkedAsPaidEnvelope>();
export const onOrderPaused = EventDefinition(
  'wix.pricing_plans.v2.order_paused',
  true,
  (event: OrderPausedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderPausedEnvelope>();
export const onOrderPurchased = EventDefinition(
  'wix.pricing_plans.v2.order_purchased',
  true,
  (event: OrderPurchasedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderPurchasedEnvelope>();
export const onOrderResumed = EventDefinition(
  'wix.pricing_plans.v2.order_resumed',
  true,
  (event: OrderResumedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderResumedEnvelope>();
export const onOrderStarted = EventDefinition(
  'wix.pricing_plans.v2.order_started',
  true,
  (event: OrderStartedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderStartedEnvelope>();
export const onOrderUpdated = EventDefinition(
  'wix.pricing_plans.v2.order_updated',
  true,
  (event: OrderUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.startDate' },
            { path: 'entity.endDate' },
            { path: 'entity.earliestEndDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.cancellation.requestedDate' },
            { path: 'entity.pausePeriods.pauseDate' },
            { path: 'entity.pausePeriods.resumeDate' },
            { path: 'entity.currentCycle.startedDate' },
            { path: 'entity.currentCycle.endedDate' },
            { path: 'entity.cycles.startedDate' },
            { path: 'entity.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderUpdatedEnvelope>();
export const onOrderCreated = EventDefinition(
  'wix.pricing_plans.v2.order_created',
  true,
  (event: OrderCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.startDate' },
            { path: 'entity.endDate' },
            { path: 'entity.earliestEndDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.cancellation.requestedDate' },
            { path: 'entity.pausePeriods.pauseDate' },
            { path: 'entity.pausePeriods.resumeDate' },
            { path: 'entity.currentCycle.startedDate' },
            { path: 'entity.currentCycle.endedDate' },
            { path: 'entity.cycles.startedDate' },
            { path: 'entity.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderCreatedEnvelope>();
export const onOrderStartDateChanged = EventDefinition(
  'wix.pricing_plans.v2.order_start_date_changed',
  true,
  (event: OrderStartDateChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.startDate' },
            { path: 'data.order.endDate' },
            { path: 'data.order.earliestEndDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.cancellation.requestedDate' },
            { path: 'data.order.pausePeriods.pauseDate' },
            { path: 'data.order.pausePeriods.resumeDate' },
            { path: 'data.order.currentCycle.startedDate' },
            { path: 'data.order.currentCycle.endedDate' },
            { path: 'data.order.cycles.startedDate' },
            { path: 'data.order.cycles.endedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<OrderStartDateChangedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  ApplyCouponRequest,
  ApplyCouponResponse,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkOrderResult,
  BulkPauseOrderRequest,
  BulkPauseOrderResponse,
  BulkResumeOrderRequest,
  BulkResumeOrderResponse,
  Buyer,
  CancelOrderRequest,
  CancelOrderResponse,
  Cancellation,
  CancellationCause,
  CancellationEffectiveAt,
  Captcha,
  ChangeStartDateRequest,
  ChangeStartDateResponse,
  Coupon,
  CouponsError,
  CreateExternalOrderRequest,
  CreateExternalOrderResponse,
  CreateGuestOnlineOrderRequest,
  CreateGuestOnlineOrderResponse,
  CreateOfflineOrderOptions,
  CreateOfflineOrderRequest,
  CreateOfflineOrderResponse,
  CreateOnlineOrderOptions,
  CreateOnlineOrderRequest,
  CreateOnlineOrderResponse,
  CurrentCycle,
  CursorPaging,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  Duration,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Fee,
  FormData,
  GetAvailableOrderActionsRequest,
  GetAvailableOrderActionsResponse,
  GetGuestOnlineOrderPreviewRequest,
  GetGuestOnlineOrderPreviewResponse,
  GetOfflineOrderPreviewOptions,
  GetOfflineOrderPreviewRequest,
  GetOfflineOrderPreviewResponse,
  GetOnlineOrderPreviewOptions,
  GetOnlineOrderPreviewRequest,
  GetOnlineOrderPreviewResponse,
  GetOrderRequest,
  GetOrderResponse,
  GetOrdersStatsRequest,
  GetOrdersStatsResponse,
  GetPricePreviewOptions,
  GetPricePreviewRequest,
  GetPricePreviewResponse,
  Guest,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListOrdersRequest,
  ListOrdersResponse,
  ManagementGetOrderOptions,
  ManagementListOrdersOptions,
  MarkAsPaidRequest,
  MarkAsPaidResponse,
  MemberGetOrderOptions,
  MemberGetOrderRequest,
  MemberGetOrderResponse,
  MemberListOrdersOptions,
  MemberListOrdersRequest,
  MemberListOrdersResponse,
  MessageEnvelope,
  OnBehalf,
  Order,
  OrderAutoRenewCanceled,
  OrderAutoRenewCanceledEnvelope,
  OrderCanceled,
  OrderCanceledEnvelope,
  OrderCreatedEnvelope,
  OrderCycle,
  OrderCycleStarted,
  OrderCycleStartedEnvelope,
  OrderEndDatePostponed,
  OrderEndDatePostponedEnvelope,
  OrderEnded,
  OrderEndedEnvelope,
  OrderMarkedAsPaid,
  OrderMarkedAsPaidEnvelope,
  OrderMethod,
  OrderPaused,
  OrderPausedEnvelope,
  OrderPurchased,
  OrderPurchasedEnvelope,
  OrderResumed,
  OrderResumedEnvelope,
  OrderStartDateChanged,
  OrderStartDateChangedEnvelope,
  OrderStarted,
  OrderStartedEnvelope,
  OrderStatus,
  OrderType,
  OrderUpdatedEnvelope,
  OrdersQueryOrdersRequest,
  OrdersQueryOrdersResponse,
  Paging,
  PagingMetadataV2,
  PauseOrderRequest,
  PauseOrderResponse,
  PausePeriod,
  PaymentStatus,
  PeriodUnit,
  PostponeEndDateRequest,
  PostponeEndDateResponse,
  Price,
  PriceDetails,
  PriceDetailsPricingModelOneOf,
  PriceDuration,
  PricingDetails,
  PricingDetailsPricingModelOneOf,
  QueryOrdersRequest,
  QueryOrdersResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  ReasonNotSuspendable,
  Recurrence,
  RequestCancellationRequest,
  RequestCancellationResponse,
  RestoreInfo,
  ResumeOrderRequest,
  ResumeOrderResponse,
  Set,
  SetSubmissionRequest,
  SetSubmissionResponse,
  SortOrder,
  Sorting,
  SpannedPrice,
  Status,
  Tax,
  WebhookIdentityType,
} from './pricing-plans-v2-order-orders.universal.js';
